import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
  useTheme,
  alpha,
  IconButton,
  Collapse,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Skeleton,
} from '@mui/material';
import {
  LocalShipping,
  Warning,
  Error as ErrorIcon,
  CheckCircle,
  Schedule,
  Science,
  MonitorHeart,
  Inventory2,
  TrendingUp,
  FilterList,
  Close,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  ExpandMore,
  ExpandLess,
  Business,
  Refresh,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  fetchAdminMonitoring,
  fetchAdminLots,
  AdminMonitoringData,
  PipelineStage,
  ClientSummary,
  CriticalLot,
  LotsFilter,
  LotsPageResult,
} from '../../services/adminMonitoringService';

const URGENCY_COLORS = {
  critical: '#ef4444',
  warning: '#f59e0b',
  ok: '#22c55e',
};

const STATUS_COLORS: Record<string, string> = {
  RECEPTION: '#3b82f6',
  PREPARATION_MECANIQUE: '#8b5cf6',
  EN_COURS_ACHEMINEMENT: '#6366f1',
  LABORATOIRE_AU_PREP_CHIMIQUE: '#f59e0b',
  LABORATOIRE_AU_ANALYSE: '#d97706',
  LABORATOIRE_ICP_PREP_CHIMIQUE: '#10b981',
  LABORATOIRE_ICP_ANALYSE: '#059669',
  LABORATOIRE_SAA_PREP_CHIMIQUE: '#ef4444',
  LABORATOIRE_SAA_ANALYSE: '#dc2626',
  LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE: '#a855f7',
  LABORATOIRE_VOLUMETRIE_ANALYSE: '#9333ea',
  VALIDATION_SERVICE: '#f97316',
  GENERATION_CERTIFICAT: '#06b6d4',
  EN_ATTENTE: '#6b7280',
};

function urgencyLevel(ageDays: number): 'critical' | 'warning' | 'ok' {
  if (ageDays > 30) return 'critical';
  if (ageDays > 14) return 'warning';
  return 'ok';
}

// ─── KPI Card ────────────────────────────────────────────────────
interface KPICardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  active?: boolean;
  glow?: boolean;
  onClick?: () => void;
}

function KPICard({ title, value, subtitle, icon, color, active, glow, onClick }: KPICardProps) {
  const theme = useTheme();
  return (
    <Paper
      elevation={active ? 8 : 1}
      onClick={onClick}
      sx={{
        flex: '1 1 0',
        minWidth: 150,
        p: 2,
        borderRadius: 3,
        cursor: onClick ? 'pointer' : 'default',
        border: active ? `2px solid ${color}` : `1px solid ${alpha(color, 0.2)}`,
        background: active
          ? `linear-gradient(135deg, ${alpha(color, 0.18)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`
          : `linear-gradient(135deg, ${alpha(color, 0.06)} 0%, ${theme.palette.background.paper} 100%)`,
        boxShadow: glow ? `0 0 24px ${alpha(color, 0.25)}` : undefined,
        transition: 'all 0.2s ease',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 20px ${alpha(color, 0.2)}`,
        } : {},
        '&:active': onClick ? { transform: 'scale(0.98)' } : {},
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" sx={{
          color: active ? color : 'text.secondary',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.65rem',
        }}>
          {title}
        </Typography>
        <Box sx={{ color: active ? color : alpha(color, 0.6) }}>{icon}</Box>
      </Box>
      <Typography variant="h4" sx={{
        fontWeight: 900,
        color,
        lineHeight: 1,
        mb: 0.5,
      }}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
          {subtitle}
        </Typography>
      )}
    </Paper>
  );
}

// ─── Transfer Tracker ────────────────────────────────────────────
function TransferTracker({ transfers }: { transfers: AdminMonitoringData['transfers'] }) {
  const theme = useTheme();
  const total = transfers.reduce((s, t) => s + t.transferCount, 0);
  const totalSamples = transfers.reduce((s, t) => s + t.sampleCount, 0);

  if (total === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2.5,
        p: 2,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.06)} 0%, ${theme.palette.background.paper} 100%)`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <LocalShipping sx={{ color: theme.palette.warning.main }} />
        <Typography variant="subtitle1" fontWeight={700}>Transferts en Transit</Typography>
        <Chip
          label={`${total} transfert${total > 1 ? 's' : ''} · ${totalSamples} éch.`}
          color="warning"
          size="small"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {transfers.map((t) => {
          const level = urgencyLevel(t.oldestTransitDays);
          return (
            <Paper key={t.fromLocationCode} variant="outlined" sx={{
              p: 1.5, flex: '1 1 200px', maxWidth: 280,
              borderColor: alpha(URGENCY_COLORS[level], 0.4),
              bgcolor: alpha(URGENCY_COLORS[level], 0.04),
              borderRadius: 2,
            }}>
              <Typography variant="subtitle2" fontWeight={800}>{t.fromLocationCode}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t.transferCount} transfert{t.transferCount > 1 ? 's' : ''} · {t.sampleCount} éch.
              </Typography>
              <Typography variant="caption" sx={{ color: URGENCY_COLORS[level], fontWeight: 700 }}>
                Moy. {t.avgTransitDays}j · Max {t.oldestTransitDays}j
              </Typography>
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
}

// ─── Pipeline Flow ───────────────────────────────────────────────
const PIPELINE_ORDER = [
  'EN_COURS_ACHEMINEMENT', 'RECEPTION', 'PREPARATION_MECANIQUE',
  'LABORATOIRE_SAA_PREP_CHIMIQUE', 'LABORATOIRE_SAA_ANALYSE',
  'LABORATOIRE_ICP_PREP_CHIMIQUE', 'LABORATOIRE_ICP_ANALYSE',
  'LABORATOIRE_AU_PREP_CHIMIQUE', 'LABORATOIRE_AU_ANALYSE',
  'LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE', 'LABORATOIRE_VOLUMETRIE_ANALYSE',
  'VALIDATION_SERVICE', 'GENERATION_CERTIFICAT', 'EN_ATTENTE',
];

function PipelineFlow({ stages }: { stages: PipelineStage[] }) {
  const theme = useTheme();
  const sorted = [...stages]
    .filter(s => s.sampleCount > 0)
    .sort((a, b) => (PIPELINE_ORDER.indexOf(a.stage) ?? 99) - (PIPELINE_ORDER.indexOf(b.stage) ?? 99));

  return (
    <Paper elevation={0} sx={{
      mb: 2.5, p: 2, borderRadius: 3,
      border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${theme.palette.background.paper} 100%)`,
    }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Science fontSize="small" color="primary" />
        Pipeline — Étapes Actives
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {sorted.map((s) => {
          const level = urgencyLevel(s.avgAgeDays);
          const stageColor = STATUS_COLORS[s.stage] || URGENCY_COLORS[level];
          return (
            <Tooltip
              key={s.stage}
              title={`${s.sampleCount} éch. · ${s.lotCount} lots · Moy. ${s.avgAgeDays}j · Max ${s.maxAgeDays}j`}
              arrow
            >
              <Paper variant="outlined" sx={{
                px: 1.5, py: 1, minWidth: 100, textAlign: 'center', borderRadius: 2,
                borderColor: alpha(stageColor, 0.4),
                borderWidth: 2,
                bgcolor: alpha(stageColor, 0.05),
                transition: 'all 0.15s',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 12px ${alpha(stageColor, 0.15)}` },
              }}>
                <Typography variant="h6" fontWeight={800} sx={{ color: stageColor, lineHeight: 1.2 }}>
                  {s.sampleCount}
                </Typography>
                <Typography variant="caption" fontWeight={600} sx={{ display: 'block', lineHeight: 1.2, mt: 0.25 }}>
                  {s.label}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                  {s.lotCount} lots · {s.avgAgeDays}j
                </Typography>
              </Paper>
            </Tooltip>
          );
        })}
      </Box>
    </Paper>
  );
}

// ─── Bottleneck Chart ────────────────────────────────────────────
function BottleneckChart({ bottlenecks }: { bottlenecks: PipelineStage[] }) {
  const theme = useTheme();
  const data = bottlenecks.map((b) => ({
    name: b.label,
    avg: b.avgAgeDays,
    max: b.maxAgeDays,
    samples: b.sampleCount,
    level: urgencyLevel(b.avgAgeDays),
  }));

  if (data.length === 0) return null;

  return (
    <Paper elevation={0} sx={{
      p: 2, borderRadius: 3, height: '100%',
      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
    }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>
        Goulets d'Étranglement
      </Typography>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} layout="vertical" margin={{ left: 10, right: 30 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" unit="j" tick={{ fontSize: 11 }} />
          <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
          <RechartsTooltip
            formatter={(value: number, name: string) => [`${value} jours`, name === 'avg' ? 'Moyenne' : 'Maximum']}
            contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 8, fontSize: 12 }}
          />
          <Bar dataKey="avg" radius={[0, 4, 4, 0]} barSize={16}>
            {data.map((entry, i) => (
              <Cell key={i} fill={URGENCY_COLORS[entry.level]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
}

// ─── Client Table (clickable for filtering) ──────────────────────
interface ClientTableProps {
  clients: ClientSummary[];
  activeClientId?: string;
  onSelectClient: (clientId: string | undefined) => void;
}

function ClientTable({ clients, activeClientId, onSelectClient }: ClientTableProps) {
  const theme = useTheme();

  if (clients.length === 0) return null;

  const sorted = [...clients].sort((a, b) => b.oldestAgeDays - a.oldestAgeDays);

  return (
    <Paper elevation={0} sx={{
      p: 0, borderRadius: 3, display: 'flex', flexDirection: 'column',
      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      overflow: 'hidden',
      maxHeight: 340,
    }}>
      <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business fontSize="small" />
          Filtrer par Client
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            (cliquer pour filtrer)
          </Typography>
        </Typography>
        {activeClientId && (
          <Chip
            label="Effacer filtre"
            size="small"
            onDelete={() => onSelectClient(undefined)}
            color="primary"
            variant="outlined"
            sx={{ height: 24 }}
          />
        )}
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pb: 1 }}>
        {sorted.map((c) => {
          const level = urgencyLevel(c.oldestAgeDays);
          const isActive = activeClientId === c.clientId;
          return (
            <Paper
              key={c.clientId}
              variant="outlined"
              onClick={() => onSelectClient(isActive ? undefined : c.clientId)}
              sx={{
                p: 1.5, mb: 0.75, cursor: 'pointer', borderRadius: 2,
                border: isActive ? `2px solid ${theme.palette.primary.main}` : `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                bgcolor: isActive ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04), borderColor: alpha(theme.palette.primary.main, 0.3) },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: 160 }}>
                  {c.clientName}
                </Typography>
                <Chip
                  label={`${c.oldestAgeDays}j`}
                  size="small"
                  sx={{ bgcolor: alpha(URGENCY_COLORS[level], 0.12), color: URGENCY_COLORS[level], fontWeight: 700, height: 22, minWidth: 40 }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {c.activeLots} lots · {c.activeSamples} éch.
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                  {c.critical > 0 && <Chip label={c.critical} size="small" sx={{ bgcolor: alpha(URGENCY_COLORS.critical, 0.15), color: URGENCY_COLORS.critical, fontWeight: 700, height: 20, minWidth: 24, '& .MuiChip-label': { px: 0.5 } }} />}
                  {c.warning > 0 && <Chip label={c.warning} size="small" sx={{ bgcolor: alpha(URGENCY_COLORS.warning, 0.15), color: URGENCY_COLORS.warning, fontWeight: 700, height: 20, minWidth: 24, '& .MuiChip-label': { px: 0.5 } }} />}
                  {c.ok > 0 && <Chip label={c.ok} size="small" sx={{ bgcolor: alpha(URGENCY_COLORS.ok, 0.15), color: URGENCY_COLORS.ok, fontWeight: 700, height: 20, minWidth: 24, '& .MuiChip-label': { px: 0.5 } }} />}
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Paper>
  );
}

// ─── Lots Table with Pagination ──────────────────────────────────
interface LotsTableProps {
  lotsData: LotsPageResult | null;
  loading: boolean;
  activeUrgency?: string;
  activeClientName?: string;
  onClearFilters: () => void;
  onPageChange: (page: number) => void;
}

function LotsTable({ lotsData, loading, activeUrgency, activeClientName, onClearFilters, onPageChange }: LotsTableProps) {
  const theme = useTheme();
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  const hasFilter = activeUrgency || activeClientName;

  if (!lotsData && !loading) return null;

  return (
    <Paper elevation={0} sx={{
      borderRadius: 3, display: 'flex', flexDirection: 'column',
      border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{
        px: 2, py: 1.5,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.divider}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.03)} 0%, ${theme.palette.background.paper} 100%)`,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Schedule fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>
            Lots en cours
          </Typography>
          {lotsData && (
            <Chip label={`${lotsData.total} lots`} size="small" sx={{ height: 22, fontWeight: 600 }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasFilter && (
            <>
              {activeUrgency && (
                <Chip
                  label={activeUrgency === 'critical' ? 'Critique' : activeUrgency === 'warning' ? 'Attention' : 'Normal'}
                  size="small"
                  sx={{
                    height: 24,
                    bgcolor: alpha(URGENCY_COLORS[activeUrgency as keyof typeof URGENCY_COLORS], 0.15),
                    color: URGENCY_COLORS[activeUrgency as keyof typeof URGENCY_COLORS],
                    fontWeight: 700,
                  }}
                  icon={<FilterList sx={{ fontSize: 14 }} />}
                />
              )}
              {activeClientName && (
                <Chip
                  label={activeClientName}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 24, fontWeight: 600 }}
                  icon={<Business sx={{ fontSize: 14 }} />}
                />
              )}
              <IconButton size="small" onClick={onClearFilters} sx={{ ml: 0.5 }}>
                <Close fontSize="small" />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {/* Column Headers */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '1.2fr 0.8fr 70px 1.2fr 70px 80px 40px',
        gap: 1, px: 2, py: 0.75,
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: alpha(theme.palette.action.hover, 0.3),
      }}>
        {['Lot', 'Client', 'Éch.', 'Étape', 'Âge', 'Urgence', ''].map((h) => (
          <Typography key={h} variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase' }}>
            {h}
          </Typography>
        ))}
      </Box>

      {/* Rows - fixed height with scroll */}
      <Box sx={{ maxHeight: 520, overflowY: 'auto', position: 'relative' }}>
        {loading && lotsData && (
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}>
            <Box sx={{ height: 2, bgcolor: alpha(theme.palette.primary.main, 0.3), overflow: 'hidden' }}>
              <Box sx={{ width: '40%', height: '100%', bgcolor: 'primary.main', animation: 'loading-bar 1s ease-in-out infinite alternate', '@keyframes loading-bar': { from: { ml: 0 }, to: { ml: '60%' } } }} />
            </Box>
          </Box>
        )}
        {loading && !lotsData ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : lotsData && lotsData.lots.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Aucun lot trouvé</Typography>
          </Box>
        ) : lotsData?.lots.map((lot, i) => {
          const urgConfig = {
            critical: { color: URGENCY_COLORS.critical, label: 'Critique', icon: <ErrorIcon sx={{ fontSize: 14 }} /> },
            warning: { color: URGENCY_COLORS.warning, label: 'Attention', icon: <Warning sx={{ fontSize: 14 }} /> },
            ok: { color: URGENCY_COLORS.ok, label: 'Normal', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
          }[lot.urgency];
          const statusColor = STATUS_COLORS[lot.statusRaw] || '#6b7280';
          const isExpanded = expandedIdx === i;

          return (
            <Box key={`${lot.batchNumber}-${i}`} sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}` }}>
              <Box
                onClick={() => setExpandedIdx(isExpanded ? null : i)}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1.2fr 0.8fr 70px 1.2fr 70px 80px 40px',
                  alignItems: 'center',
                  gap: 1, px: 2, py: 1,
                  cursor: 'pointer',
                  borderLeft: `3px solid ${urgConfig.color}`,
                  bgcolor: isExpanded ? alpha(urgConfig.color, 0.06) : 'transparent',
                  transition: 'background 0.15s',
                  '&:hover': { bgcolor: alpha(urgConfig.color, 0.04) },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: urgConfig.color, fontFamily: 'monospace' }}>
                  {lot.batchNumber || 'N/A'}
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontSize: '0.78rem' }}>
                  {lot.clientName}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'center' }}>
                  {lot.sampleCount}
                </Typography>
                <Chip
                  label={lot.status}
                  size="small"
                  sx={{
                    bgcolor: alpha(statusColor, 0.12),
                    color: statusColor,
                    fontWeight: 700,
                    fontSize: '0.6rem',
                    height: 22,
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
                <Typography variant="body2" sx={{
                  fontWeight: 800, textAlign: 'center',
                  color: urgConfig.color,
                  fontSize: '0.85rem',
                }}>
                  {lot.ageDays}j
                </Typography>
                <Chip
                  icon={urgConfig.icon}
                  label={urgConfig.label}
                  size="small"
                  sx={{
                    bgcolor: alpha(urgConfig.color, 0.12),
                    color: urgConfig.color,
                    fontWeight: 700,
                    fontSize: '0.55rem',
                    height: 22,
                    '& .MuiChip-label': { px: 0.5 },
                    '& .MuiChip-icon': { color: urgConfig.color },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  {isExpanded ? <ExpandLess fontSize="small" color="action" /> : <ExpandMore fontSize="small" color="action" />}
                </Box>
              </Box>

              <Collapse in={isExpanded}>
                <Box sx={{
                  px: 3, py: 1.5,
                  borderLeft: `3px solid ${urgConfig.color}`,
                  bgcolor: alpha(theme.palette.action.hover, 0.3),
                }}>
                  <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Reçu le</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {lot.received ? new Date(lot.received).toLocaleDateString('fr-FR') : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Dernière modification</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {lot.lastModified ? new Date(lot.lastModified).toLocaleDateString('fr-FR') : '-'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Étape actuelle</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ color: statusColor }}>
                        {lot.status}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Échantillons</Typography>
                      <Typography variant="body2" fontWeight={600}>{lot.sampleCount}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Box>

      {/* Pagination */}
      {lotsData && lotsData.totalPages > 1 && (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2,
          py: 1.5, px: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}>
          <IconButton
            onClick={() => onPageChange(lotsData.page - 1)}
            disabled={lotsData.page <= 1}
            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), width: 40, height: 40, borderRadius: 2 }}
          >
            <KeyboardArrowLeft />
          </IconButton>
          <Typography variant="body2" fontWeight={700} color="text.secondary">
            {lotsData.page} / {lotsData.totalPages}
          </Typography>
          <IconButton
            onClick={() => onPageChange(lotsData.page + 1)}
            disabled={lotsData.page >= lotsData.totalPages}
            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), width: 40, height: 40, borderRadius: 2 }}
          >
            <KeyboardArrowRight />
          </IconButton>
        </Box>
      )}
    </Paper>
  );
}

// ─── Main Component ──────────────────────────────────────────────
export default function LabMonitoringPanel() {
  const theme = useTheme();
  const [data, setData] = useState<AdminMonitoringData | null>(null);
  const [lotsData, setLotsData] = useState<LotsPageResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activeUrgency, setActiveUrgency] = useState<'critical' | 'warning' | 'ok' | undefined>();
  const [activeClientId, setActiveClientId] = useState<string | undefined>();
  const [lotsPage, setLotsPage] = useState(1);

  const activeClientName = useMemo(() => {
    if (!activeClientId || !data) return undefined;
    return data.clients.find(c => c.clientId === activeClientId)?.clientName;
  }, [activeClientId, data]);

  const loadOverview = useCallback(async () => {
    try {
      const result = await fetchAdminMonitoring();
      setData(result);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLots = useCallback(async () => {
    setLotsLoading(true);
    try {
      const result = await fetchAdminLots({
        urgency: activeUrgency,
        clientId: activeClientId,
        page: lotsPage,
        limit: 15,
      });
      setLotsData(result);
    } catch (e: any) {
      console.error('Failed to load lots:', e);
    } finally {
      setLotsLoading(false);
    }
  }, [activeUrgency, activeClientId, lotsPage]);

  useEffect(() => {
    loadOverview();
    const interval = setInterval(loadOverview, 60_000);
    return () => clearInterval(interval);
  }, [loadOverview]);

  useEffect(() => {
    loadLots();
  }, [loadLots]);

  const handleFilterUrgency = useCallback((urgency: 'critical' | 'warning' | 'ok') => {
    setActiveUrgency(prev => prev === urgency ? undefined : urgency);
    setLotsPage(1);
  }, []);

  const handleFilterClient = useCallback((clientId: string | undefined) => {
    setActiveClientId(clientId);
    setLotsPage(1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setActiveUrgency(undefined);
    setActiveClientId(undefined);
    setLotsPage(1);
  }, []);

  if (loading && !data) {
    return (
      <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8, borderRadius: 3 }}>
        <CircularProgress size={32} sx={{ mr: 2 }} />
        <Typography color="text.secondary">Chargement du monitoring…</Typography>
      </Paper>
    );
  }

  if (error && !data) {
    return <Alert severity="error" sx={{ mb: 4, borderRadius: 3 }}>{error}</Alert>;
  }

  if (!data) return null;

  const totalActiveLots = data.urgency.critical.lots + data.urgency.warning.lots + data.urgency.ok.lots;
  const totalActiveSamples = data.urgency.critical.samples + data.urgency.warning.samples + data.urgency.ok.samples;
  const oldest = data.criticalLots[0];

  return (
    <Box>
      {/* Title bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
        <Typography variant="h6" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MonitorHeart color="primary" />
          Monitoring Laboratoire
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`Mis à jour ${new Date(data.generatedAt).toLocaleTimeString('fr-FR')}`}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
          <IconButton size="small" onClick={loadOverview} sx={{ ml: 0.5 }}>
            <Refresh fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* KPI Strip - clickable urgency cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
        <KPICard
          title="Lots Actifs"
          value={totalActiveLots}
          subtitle={`${totalActiveSamples.toLocaleString()} échantillons`}
          icon={<Inventory2 sx={{ fontSize: 20 }} />}
          color="#6366f1"
        />
        <KPICard
          title="Critique >30j"
          value={data.urgency.critical.lots}
          subtitle={`${data.urgency.critical.samples.toLocaleString()} éch.`}
          icon={<ErrorIcon sx={{ fontSize: 20 }} />}
          color={URGENCY_COLORS.critical}
          active={activeUrgency === 'critical'}
          glow={data.urgency.critical.lots > 0}
          onClick={() => handleFilterUrgency('critical')}
        />
        <KPICard
          title="Attention 14-30j"
          value={data.urgency.warning.lots}
          subtitle={`${data.urgency.warning.samples.toLocaleString()} éch.`}
          icon={<Warning sx={{ fontSize: 20 }} />}
          color={URGENCY_COLORS.warning}
          active={activeUrgency === 'warning'}
          onClick={() => handleFilterUrgency('warning')}
        />
        <KPICard
          title="Normal <14j"
          value={data.urgency.ok.lots}
          subtitle={`${data.urgency.ok.samples.toLocaleString()} éch.`}
          icon={<CheckCircle sx={{ fontSize: 20 }} />}
          color={URGENCY_COLORS.ok}
          active={activeUrgency === 'ok'}
          onClick={() => handleFilterUrgency('ok')}
        />
        {oldest && (
          <KPICard
            title="Plus ancien"
            value={`${oldest.ageDays}j`}
            subtitle={oldest.batchNumber}
            icon={<TrendingUp sx={{ fontSize: 20 }} />}
            color={oldest.ageDays > 60 ? URGENCY_COLORS.critical : URGENCY_COLORS.warning}
            glow={oldest.ageDays > 90}
          />
        )}
      </Box>

      {/* Transfers */}
      <TransferTracker transfers={data.transfers} />

      {/* Pipeline */}
      <PipelineFlow stages={data.pipeline} />

      {/* Clients filter bar + Bottleneck */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={7}>
          <ClientTable
            clients={data.clients}
            activeClientId={activeClientId}
            onSelectClient={handleFilterClient}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <BottleneckChart bottlenecks={data.bottlenecks} />
        </Grid>
      </Grid>

      {/* Lots Table with pagination and filters */}
      <LotsTable
        lotsData={lotsData}
        loading={lotsLoading}
        activeUrgency={activeUrgency}
        activeClientName={activeClientName}
        onClearFilters={handleClearFilters}
        onPageChange={setLotsPage}
      />
    </Box>
  );
}
